import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import { Formik } from "formik";
import { AddClientSchema } from "validations";
import useCommon from "hooks/useCommon";
import VuiAlert from "components/VuiAlert";
import VuiPhoneInput from "components/VuiPhoneInput";
import { FaUpload, FaInfoCircle } from "react-icons/fa";
import { prepareBrandLogoFromFile } from "services/uploadLogo";
import { MAX_HOTELS_PER_CLIENT } from "variables";
import { removeEmptyKeys } from "utils/objectHelpers";
import OverlayLoader from "components/OverlayLoader";
import TimezoneSelectField from "components/TimeZoneSelector";

const FieldLabel = ({ title, info, required = false }) => (
  <VuiBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
    <VuiTypography variant="caption">
      {title}
      {required ? " *" : ""}
    </VuiTypography>

    {info && (
      <Tooltip title={info} placement="top" arrow>
        <VuiBox
          component="span"
          display="inline-flex"
          alignItems="center"
          sx={{
            cursor: "help",
          }}
        >
          <FaInfoCircle size={12} />
        </VuiBox>
      </Tooltip>
    )}
  </VuiBox>
);

const createEmptyHotel = () => ({
  name: "",
  address: "",
  timezone: "",
  defaultPhoneNumber: "",
  defaultEmail: "",
  identifierMode: "phone",
  countryCode: "",
  brandLogo: {
    contentType: "",
    dataBase64: "",
  },
});

const AddNewClientDialog = ({ open, setOpen, formData, setFormData, onClose, onSubmit }) => {
  const { STORE } = useCommon();
  const { createLoading, createError } = STORE((state) => state.clients);

  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadErrors, setUploadErrors] = useState({});
  const [hotelLimitWarning, setHotelLimitWarning] = useState(null);

  const dialogPaperRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddHotel = (values, setFieldValue) => {
    if (values.hotels.length >= MAX_HOTELS_PER_CLIENT) {
      setHotelLimitWarning(`You can only add up to ${MAX_HOTELS_PER_CLIENT} hotels per client.`);

      setTimeout(() => {
        setHotelLimitWarning(null);
      }, 3000);

      return;
    }

    setHotelLimitWarning(null);

    setFieldValue("hotels", [...values.hotels, createEmptyHotel()]);
  };

  const handleCloseDialog = () => {
    setUploadErrors({});
    setUploadingIndex(null);
    setOpen(false);
    onClose();
  };

  const handleBrandLogoUpload = async (file, index, setFieldValue) => {
    if (!file) return;

    setUploadingIndex(index);

    setUploadErrors((prev) => ({
      ...prev,
      [index]: null,
    }));

    try {
      const logoObject = await prepareBrandLogoFromFile(file);
      setFieldValue(`hotels.${index}.brandLogo`, logoObject);
    } catch (err) {
      setUploadErrors((prev) => ({
        ...prev,
        [index]: err?.response?.data?.message || err?.message || "Failed to upload logo",
      }));
    } finally {
      setUploadingIndex(null);
    }
  };

  useEffect(() => {
    if (createLoading) {
      // Scroll dialog content to top
      if (dialogPaperRef.current) {
        dialogPaperRef.current.scrollTop = 0;
      }

      // Lock page scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [createLoading]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        handleCloseDialog();
      }}
      PaperProps={{
        ref: dialogPaperRef,
        sx: {
          overflow: createLoading ? "hidden" : "auto",
        },
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <VuiTypography variant="button" fontWeight="bold">
          Add New Client
        </VuiTypography>
      </DialogTitle>

      <Formik
        initialValues={formData}
        validationSchema={AddClientSchema}
        onSubmit={(values) => {
          // Clean  hotel object
          const cleanedHotels = values.hotels.map((hotel) => removeEmptyKeys(hotel));
          const cleanedPayload = removeEmptyKeys({
            ...values,
            hotels: cleanedHotels,
          });

          onSubmit(cleanedPayload);
        }}
        // onSubmit={(values) => {
        //   onSubmit(values);
        // }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldTouched,
          setFieldValue,
        }) => {
          const handleRemoveHotel = (index) => {
            const updatedHotels = values.hotels.filter((_, i) => i !== index);
            setFieldValue("hotels", updatedHotels);
          };
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ pt: 0 }}>
                {createError && (
                  <VuiAlert color="error" dismissible size="sm">
                    {createError ? createError : createError.message}
                  </VuiAlert>
                )}
                <VuiBox
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                  gap="16px"
                  mb={2}
                >
                  <VuiBox>
                    <VuiTypography variant="caption">Client Name *</VuiTypography>
                    <VuiInput
                      gradient
                      textOnly
                      fullWidth
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={touched.name && Boolean(errors.name)}
                      inputProps={{ maxLength: 30 }}
                    />
                    {touched.name && errors.name && (
                      <VuiTypography variant="caption" color="error">
                        {errors.name}
                      </VuiTypography>
                    )}
                  </VuiBox>

                  <VuiBox>
                    <VuiTypography variant="caption">Email *</VuiTypography>
                    <VuiInput
                      gradient
                      fullWidth
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      inputProps={{ maxLength: 80 }}
                    />
                    {touched.email && errors.email && (
                      <VuiTypography variant="caption" color="error">
                        {errors.email}
                      </VuiTypography>
                    )}
                  </VuiBox>
                </VuiBox>

                <VuiBox mb={1}>
                  <VuiPhoneInput
                    label="Contact Number *"
                    value={values.contactNumber}
                    defaultCountry="US"
                    onChange={(value) => {
                      setFieldValue("contactNumber", value || "");
                    }}
                    onBlur={() => setFieldTouched("contactNumber", true)}
                    // onChange={(value) =>
                    //   handleChange({
                    //     target: {
                    //       name: "contactNumber",
                    //       value,
                    //     },
                    //   })
                    // }
                    error={touched.contactNumber && errors.contactNumber}
                  />
                </VuiBox>

                {/* Hotels */}
                <VuiTypography variant="button" fontWeight="medium" mb={1}>
                  Hotels *
                </VuiTypography>

                {values.hotels.map((hotel, index) => (
                  <VuiBox
                    key={index}
                    mb={2}
                    sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: "12px", p: 2 }}
                  >
                    <VuiBox display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
                      {values.hotels.length > 1 && (
                        <VuiButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveHotel(index)}
                          sx={{ minWidth: "auto", px: 1 }}
                        >
                          Remove
                        </VuiButton>
                      )}
                    </VuiBox>

                    <VuiBox mb={1}>
                      <VuiTypography variant="caption">Hotel Name *</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        value={hotel.name}
                        onChange={(e) => setFieldValue(`hotels.${index}.name`, e.target.value)}
                      />
                      {touched.hotels?.[index]?.name && errors.hotels?.[index]?.name && (
                        <VuiTypography variant="caption" color="error">
                          {errors.hotels[index].name}
                        </VuiTypography>
                      )}
                    </VuiBox>

                    <VuiBox mb={1}>
                      <VuiTypography variant="caption">Address *</VuiTypography>
                      <VuiInput
                        gradient
                        fullWidth
                        value={hotel.address}
                        onChange={(e) => setFieldValue(`hotels.${index}.address`, e.target.value)}
                      />
                      {touched.hotels?.[index]?.address && errors.hotels?.[index]?.address && (
                        <VuiTypography variant="caption" color="error">
                          {errors.hotels[index].address}
                        </VuiTypography>
                      )}
                    </VuiBox>

                    <VuiBox mb={1}>
                      <TimezoneSelectField
                        value={hotel.timezone}
                        onChange={(value) => setFieldValue(`hotels.${index}.timezone`, value)}
                        touched={touched.hotels?.[index]?.timezone}
                        error={errors.hotels?.[index]?.timezone}
                      />
                    </VuiBox>

                    {/* <VuiBox mb={1}>
                      <FieldLabel
                        title="Identifier Mode"
                        info="Determines how the guests of this hotel will receive survey links"
                      />

                      <FormControl>
                        <RadioGroup
                          row
                          value={hotel.identifierMode || "phone"}
                          onChange={(event) =>
                            setFieldValue(`hotels.${index}.identifierMode`, event.target.value)
                          }
                        >
                          <FormControlLabel
                            value="phone"
                            control={<Radio size="small" />}
                            label="Phone"
                          />

                          <FormControlLabel
                            value="email"
                            control={<Radio size="small" />}
                            label="Email"
                          />
                        </RadioGroup>
                      </FormControl>

                      {touched.hotels?.[index]?.identifierMode &&
                        errors.hotels?.[index]?.identifierMode && (
                          <VuiTypography variant="caption" color="error">
                            {errors.hotels[index].identifierMode}
                          </VuiTypography>
                        )}
                    </VuiBox> */}

                    {/* {hotel.identifierMode === "phone" && (
                      <VuiBox
                        display="grid"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "140px minmax(0, 1fr)",
                        }}
                        gap="16px"
                        mb={1}
                      >
                        <VuiBox>
                          <FieldLabel title="Country Code" />

                          <VuiInput
                            gradient
                            fullWidth
                            name={`hotels.${index}.countryCode`}
                            placeholder="+1"
                            value={hotel.countryCode || ""}
                            onChange={handleChange}
                            onBlur={() => setFieldTouched(`hotels.${index}.countryCode`, true)}
                            error={
                              touched.hotels?.[index]?.countryCode &&
                              Boolean(errors.hotels?.[index]?.countryCode)
                            }
                            inputProps={{
                              maxLength: 6,
                            }}
                          />

                          {touched.hotels?.[index]?.countryCode &&
                            errors.hotels?.[index]?.countryCode && (
                              <VuiTypography variant="caption" color="error">
                                {errors.hotels[index].countryCode}
                              </VuiTypography>
                            )}
                        </VuiBox>

                        <VuiBox>
                          <FieldLabel title="Default Phone Number" />

                          <VuiInput
                            gradient
                            fullWidth
                            type="tel"
                            name={`hotels.${index}.defaultPhoneNumber`}
                            placeholder="555 123 4567"
                            value={hotel.defaultPhoneNumber || ""}
                            onChange={handleChange}
                            onBlur={() =>
                              setFieldTouched(`hotels.${index}.defaultPhoneNumber`, true)
                            }
                            error={
                              touched.hotels?.[index]?.defaultPhoneNumber &&
                              Boolean(errors.hotels?.[index]?.defaultPhoneNumber)
                            }
                            inputProps={{
                              maxLength: 20,
                            }}
                          />

                          {touched.hotels?.[index]?.defaultPhoneNumber &&
                            errors.hotels?.[index]?.defaultPhoneNumber && (
                              <VuiTypography variant="caption" color="error">
                                {errors.hotels[index].defaultPhoneNumber}
                              </VuiTypography>
                            )}
                        </VuiBox>
                      </VuiBox>
                    )}

                    {hotel.identifierMode === "email" && (
                      <VuiBox mb={1}>
                        <FieldLabel title="Default Email" />

                        <VuiInput
                          gradient
                          fullWidth
                          type="email"
                          name={`hotels.${index}.defaultEmail`}
                          placeholder="hotel@example.com"
                          value={hotel.defaultEmail || ""}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched(`hotels.${index}.defaultEmail`, true)}
                          error={
                            touched.hotels?.[index]?.defaultEmail &&
                            Boolean(errors.hotels?.[index]?.defaultEmail)
                          }
                          inputProps={{
                            maxLength: 80,
                          }}
                        />

                        {touched.hotels?.[index]?.defaultEmail &&
                          errors.hotels?.[index]?.defaultEmail && (
                            <VuiTypography variant="caption" color="error">
                              {errors.hotels[index].defaultEmail}
                            </VuiTypography>
                          )}
                      </VuiBox>
                    )} */}

                    <VuiBox>
                      <VuiTypography variant="caption" mb={1}>
                        Brand Logo
                      </VuiTypography>

                      {hotel.brandLogo?.dataBase64 && (
                        <VuiBox mb={1}>
                          <VuiBox display="flex" justifyContent="center" mb={1}>
                            <img
                              src={hotel.brandLogo.dataBase64}
                              alt="Brand Logo"
                              style={{
                                height: 88,
                                borderRadius: 6,
                                border: "1px solid rgba(255,255,255,0.2)",
                              }}
                            />
                          </VuiBox>

                          <VuiBox display="flex" justifyContent="center">
                            <VuiButton
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => {
                                setFieldValue(`hotels.${index}.brandLogo`, null);
                              }}
                            >
                              Remove Logo
                            </VuiButton>
                          </VuiBox>
                        </VuiBox>
                      )}
                      <VuiBox display="flex" justifyContent="center">
                        <VuiButton
                          component="label"
                          // size="small"
                          color={hotel?.brandLogo?.dataBase64 ? "success" : "primary"}
                          variant="outlined"
                          disabled={uploadingIndex === index}
                        >
                          <FaUpload size={12} style={{ marginRight: 8 }} />
                          {uploadingIndex === index
                            ? "Uploading..."
                            : hotel?.brandLogo?.dataBase64
                              ? "Update Logo"
                              : "Upload Logo"}

                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg"
                            onChange={(e) =>
                              handleBrandLogoUpload(e.target.files[0], index, setFieldValue)
                            }
                          />
                        </VuiButton>
                      </VuiBox>
                      {/* Upload error */}
                      {uploadErrors[index] && (
                        <VuiBox mt={1} display="flex" justifyContent="center">
                          <VuiTypography variant="caption" color="error" textAlign="center">
                            {uploadErrors[index]}
                          </VuiTypography>
                        </VuiBox>
                      )}
                    </VuiBox>
                  </VuiBox>
                ))}

                {typeof errors.hotels === "string" && (
                  <VuiTypography variant="caption" color="error">
                    {errors.hotels}
                  </VuiTypography>
                )}

                <VuiButton
                  variant="outlined"
                  color="primary"
                  onClick={() => handleAddHotel(values, setFieldValue)}
                >
                  + Add Hotel
                </VuiButton>
                {hotelLimitWarning && (
                  <VuiBox mt={1} display="flex" justifyContent="center">
                    <VuiTypography variant="caption" color="warning" textAlign="center">
                      {hotelLimitWarning}
                    </VuiTypography>
                  </VuiBox>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <VuiButton variant="outlined" color="primary" onClick={handleCloseDialog}>
                  Cancel
                </VuiButton>

                <VuiButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={createLoading}
                >
                  {createLoading ? "Adding..." : "Add Client"}
                </VuiButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>

      <OverlayLoader open={createLoading} message="Creating new client..." />
    </Dialog>
  );
};

export default AddNewClientDialog;
